/**
 * What a land-registry transaction transferred, in one line, built from the
 * structured `transaction_properties` only. The registry's own sentence
 * (`transaction_step_text`) says the same thing but names the parties, so it
 * is never read.
 *
 *   "PPE 131/1000, lot Genève-Eaux-Vives/22/2907-8: appartement 6.01, balcons, cave 1.09"
 *   "Propriété individuelle, parcel 3120, 1641 m2 land"
 */

const isRow = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const text = (v: unknown): string | undefined => {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  if (typeof v !== 'string') return undefined
  const t = v.trim()
  return t === '' ? undefined : t
}

const positive = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined

const describeUnit = (u: unknown): string | undefined => {
  if (!isRow(u)) return undefined
  const type = text(u.unit_type)
  const plan = text(u.unit_plan_number)
  if (!type && !plan) return undefined
  const area = positive(u.unit_area)
  let s = type ?? 'unit'
  if (plan) s += ` ${plan}`
  if (area) s += ` (${area} m2)`
  return s
}

const describeProperty = (p: unknown): string | undefined => {
  if (!isRow(p)) return undefined
  const head: string[] = []
  const legalType = text(p.legal_type)
  const share = text(p.share_thousands) ?? text(p.property_share)
  if (legalType) head.push(share ? `${legalType} ${share}` : legalType)
  else if (share) head.push(`share ${share}`)

  // The lot id carries the parcel number already; fall back to the parcel.
  const lot = text(p.legal_id)
  const parcel = text(p.land_code_number)
  if (lot) head.push(`lot ${lot}`)
  else if (parcel) head.push(`parcel ${parcel}`)

  const transferred = positive(p.surface_area_transferred)
  const landArea = positive(p.land_area)
  if (transferred) head.push(`${transferred} m2 transferred`)
  // In a PPE the land area is the whole building's parcel, not the lot's.
  else if (landArea && legalType !== 'PPE') head.push(`${landArea} m2 land`)

  const floor = positive(p.floor_nb)
  if (floor) head.push(`floor ${floor}`)

  const units = Array.isArray(p.units)
    ? p.units.map(describeUnit).filter((u): u is string => Boolean(u))
    : []

  if (head.length === 0 && units.length === 0) return undefined
  if (units.length === 0) return head.join(', ')
  return head.length
    ? `${head.join(', ')}: ${units.join(', ')}`
    : units.join(', ')
}

/** One line per property, separated by "; ". `undefined` when nothing is known. */
export const describeTransactionProperties = (
  raw: unknown
): string | undefined => {
  if (!Array.isArray(raw)) return undefined
  const parts = raw.map(describeProperty).filter((p): p is string => Boolean(p))
  return parts.length ? parts.join('; ') : undefined
}
